/**
 * used to enforce that the supplied key of T is of a specific type, V
 *
 * @example
 * interface Foo {
 *  a: string
 *  b: number
 *  c: boolean
 * }
 *
 * function doSomethingWithString(foo: Foo, stringProp: KeysOfType<Foo, string>) {
 *  ...
 * }
 *
 * doSomethingWithString(foo, 'a') // all good
 * doSomethingWithString(foo, 'b') // error
 */
export type KeysOfType<T, V> = keyof {
  [P in keyof T as T[P] extends V ? P : never]: any
}

/**
 * used to enforce that the supplied key of T is of a specific type, V
 *
 * allows undefined as well as V
 *
 * @example
 * interface Foo {
 *  a?: string
 *  b: number
 *  c: boolean
 * }
 *
 * function doSomethingWithString(foo: Foo, stringProp: OptionalKeysOfType<Foo, string>) {
 *  ...
 * }
 *
 * doSomethingWithString(foo, 'a') // all good
 * doSomethingWithString(foo, 'b') // error
 */
export type OptionalKeysOfType<T, V> = keyof {
  [P in keyof T as T[P] extends V | undefined ? P : never]: any
} &
  string

/**
 * used to enforce that the supplied key of T is of a specific type, V
 *
 * allows null as well as V
 *
 * @example
 * interface Foo {
 *  a: string | null
 *  b: number
 *  c: boolean
 * }
 *
 * function doSomethingWithString(foo: Foo, stringProp: NullableKeysOfType<Foo, string>) {
 *  ...
 * }
 *
 * doSomethingWithString(foo, 'a') // all good
 * doSomethingWithString(foo, 'b') // error
 */
export type NullableKeysOfType<T, V> = keyof {
  [P in keyof T as T[P] extends V | null ? P : never]: any
} &
  string

/**
 * used to enforce that the supplied key of T is of a specific type, V
 *
 * allows null and undefined as well as V
 *
 * @example
 * interface Foo {
 *  a?: string | null
 *  b: number
 *  c: boolean
 * }
 *
 * function doSomethingWithString(foo: Foo, stringProp: OptionalNullableKeysOfType<Foo, string>) {
 *  ...
 * }
 *
 * doSomethingWithString(foo, 'a') // all good
 * doSomethingWithString(foo, 'b') // error
 */
export type OptionalNullableKeysOfType<T, V> = keyof {
  [P in keyof T as T[P] extends V | undefined | null ? P : never]: any
} &
  string

export const enabled = true as const
export const required = true as const
